import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  designatePickup,
  getLogisticsQueue,
  shipOrder,
  labelUrlFor,
  type ShipPayload,
  type LogisticsQueueOrder,
} from '../api/orders';
import { ApiError } from '../api/client';
import { isDemoMode } from '../admin/demoMode';
import { DemoBanner } from '../admin/DemoBanner';
import { PARCEL_SIZES, parcelSizeForShirtCount, type ParcelSize } from '../util/parcelSize';
import { OwnLabel, printOwnLabel } from './OwnLabel';

// Mirrors KURIER_DIMENSIONS in apps/api/src/integrations/shipx.ts (cm).
const SIZE_DIMENSIONS: Record<ParcelSize, string> = {
  A: '8×38×64',
  B: '19×38×64',
  C: '41×38×64',
};

const SHIP_ERROR_MESSAGE: Record<string, string> = {
  not_paid: 'Zamówienie nie jest opłacone — nie można go nadać.',
  method_mismatch: 'Metoda dostawy nie zgadza się z tą wybraną przez klienta.',
  paczkomat_no_d_size: 'Paczkomat nie obsługuje gabarytu D.',
  kurier_no_d_size: 'Gabaryt D nie zmieści się w paczkomacie nadawczym — podziel przesyłkę.',
  courier_dropoff_not_configured:
    'Nie ustawiono paczkomatu nadawczego dla kuriera. Uzupełnij go w Ustawieniach.',
  pickup_use_designate_endpoint: 'To zamówienie jest do odbioru osobistego, nie do wysyłki.',
  already_pickup: 'To zamówienie jest już spakowane do odbioru osobistego.',
  not_pickup_method: 'Klient nie wybrał odbioru osobistego — odśwież kolejkę.',
  no_tracking_number:
    'Zamówienie zagraniczne nie ma numeru przesyłki przewoźnika. Wpisz go na stronie zamówienia.',
  international_own_label_only: 'Zamówienie zagraniczne nie jedzie z InPostem — odśwież kolejkę.',
  pending_ride_alongs:
    'Na tę paczkę czeka nieopłacone zamówienie. Rozwiąż je na stronie zamówienia, zanim ją spakujesz.',
  invalid_body: 'Nieprawidłowe dane nadania.',
  not_found: 'Nie znaleziono zamówienia.',
  label_not_ready: 'InPost jeszcze nie wygenerował etykiety — spróbuj wydrukować za chwilę.',
  shipment_processing:
    'InPost nadal nie przydzielił numeru przesyłki. Paczka jest zamówiona — sprawdź jej status w panelu InPost.',
};

// ShipX refusal codes worth naming: each one is an account problem the operator cannot fix by
// re-sending the parcel.
const SHIPX_REJECTION_MESSAGE: Record<string, string> = {
  debt_collection:
    'InPost wstrzymał nadania z powodu nieuregulowanych płatności. Opłać zaległe faktury w panelu InPost, a następnie spróbuj ponownie.',
  insufficient_funds: 'Za mało środków na koncie InPost — doładuj je i nadaj ponownie.',
};

function shipErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'Brak połączenia z serwerem — nadanie nie powiodło się.';
  }
  if (err.status === 401) return 'Sesja wygasła — zaloguj się ponownie.';
  const code = (err.body.error as string | undefined) ?? err.message;
  if (code === 'shipx_rejected') {
    const reason = (err.body.code as string | undefined) ?? '';
    return (
      SHIPX_REJECTION_MESSAGE[reason] ??
      `InPost odrzucił nadanie: ${reason || ((err.body.message as string | undefined) ?? 'brak szczegółów')}`
    );
  }
  if (code === 'shipx_failed') {
    return `Nie udało się połączyć z InPostem: ${(err.body.message as string | undefined) ?? 'brak szczegółów'}`;
  }
  return SHIP_ERROR_MESSAGE[code] ?? `Nadanie nie powiodło się (HTTP ${err.status}).`;
}

function isProcessing(err: unknown): boolean {
  return err instanceof ApiError && err.body.error === 'shipment_processing';
}

// How long the kiosk keeps waiting on its own. The API answers a pending parcel in ~20s, and
// re-sending resumes that same shipment instead of buying another, so this is a wait, not a
// retry loop the operator has to drive.
const SHIP_WAIT_TOTAL_MS = 120_000;

/**
 * Ships an order and keeps waiting until InPost has issued a tracking number.
 *
 * The API bounds each call to stay inside its own request limit, so a pending parcel comes back
 * as `shipment_processing`. Re-sending resumes the shipment the first call created; only a
 * rejection or a real transport failure stops the wait.
 */
async function shipUntilNumbered(
  orderId: string,
  payload: ShipPayload,
  onWaiting: (waitedMs: number) => void,
): Promise<Awaited<ReturnType<typeof shipOrder>>> {
  const startedAt = Date.now();
  for (;;) {
    try {
      return await shipOrder(orderId, payload);
    } catch (err) {
      const waitedMs = Date.now() - startedAt;
      if (!isProcessing(err) || waitedMs >= SHIP_WAIT_TOTAL_MS) throw err;
      onWaiting(waitedMs);
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
}

type Notice = {
  kind: 'error' | 'info';
  text: string;
  labelUrl?: string;
  /** An own label lives only in the window it was printed in, so a lost print is redone here. */
  reprint?: LogisticsQueueOrder;
};

/** Pickup and foreign parcels never travel with InPost, so they get the shop's own label. */
function hasOwnLabel(order: LogisticsQueueOrder): boolean {
  return order.shippingMethod === 'odbior' || order.shippingMethod === 'zagranica';
}

const METHOD_TAG: Record<LogisticsQueueOrder['shippingMethod'], string> = {
  paczkomat: 'PACZKOMAT',
  kurier: 'KURIER',
  odbior: 'ODBIÓR OSOBISTY',
  zagranica: 'ZAGRANICA',
};

export function Kiosk() {
  const navigate = useNavigate();
  const demo = isDemoMode();
  const [queue, setQueue] = useState<LogisticsQueueOrder[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [sizeOverride, setSizeOverride] = useState<{ orderId: string; size: ParcelSize } | null>(
    null,
  );
  const [sentToday, setSentToday] = useState(0);
  const [now, setNow] = useState(() => new Date());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  // State updates are async, so a second Enter within the same tick would still pass a `busy`
  // check and send the parcel twice.
  const inFlight = useRef(false);

  useEffect(() => {
    getLogisticsQueue()
      .then((r) => {
        setQueue(r.queue);
        setSentToday(r.sentToday);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const order = queue[cursor];
  // Counts merged-in shirts too: they travel in the same box, and the queue already
  // folds them into items[].
  const totalShirts = order ? order.items.reduce((s, i) => s + i.qty, 0) : 0;
  const suggestedSize = parcelSizeForShirtCount(totalShirts);
  // Keyed by order id rather than cleared on cursor change: an effect would reset a render too
  // late, and Enter ships the parcel on that render.
  const overridden = sizeOverride !== null && sizeOverride.orderId === order?.id;
  const size = overridden ? sizeOverride.size : suggestedSize;

  const skip = () => {
    setNotice(null);
    setCursor((c) => Math.min(c + 1, queue.length));
  };

  // A pickup box is packed but not sent, and the API's sentToday counts shippedAt.
  const advance = (sent = true) => {
    if (sent) setSentToday((n) => n + 1);
    setCursor((c) => Math.min(c + 1, queue.length));
  };

  const reprintOwnLabel = (target: LogisticsQueueOrder) => {
    const labelWindow = window.open('', '_blank');
    if (labelWindow) printOwnLabel(labelWindow, target, demo);
  };

  const packUnderOwnLabel = async (target: LogisticsQueueOrder) => {
    // Opened before any await, or a popup blocker kills it. Printed only after the request: a label
    // for an order the API refused would put a box in the wrong pile.
    const labelWindow = window.open('', '_blank');
    labelWindow?.document.write(
      '<!doctype html><title>Etykieta</title>' +
        '<body style="font:16px system-ui;padding:24px">Przygotowuję etykietę…</body>',
    );
    try {
      // Demo mode refuses every admin write, so only the label is printed.
      if (!demo) {
        if (target.shippingMethod === 'odbior') await designatePickup(target.id);
        else await shipOrder(target.id, { mode: 'own_label', method: 'zagranica' });
      }
      if (labelWindow) printOwnLabel(labelWindow, target, demo);
      const done = demo
        ? `Tryb demo: wydruk etykiety ${target.id} — nic nie zostało zmienione.`
        : target.shippingMethod === 'odbior'
          ? `Spakowano ${target.id} do odbioru osobistego.`
          : `Nadano ${target.id} za granicę · ${target.trackingNumber}.`;
      setNotice({
        kind: 'info',
        text: labelWindow ? done : `${done} Przeglądarka zablokowała okno etykiety.`,
        reprint: target,
      });
      advance(target.shippingMethod !== 'odbior');
    } catch (err) {
      console.error('[kiosk] own-label pack failed', err);
      labelWindow?.close();
      setNotice({ kind: 'error', text: shipErrorMessage(err) });
    }
  };

  const print = async () => {
    if (!order || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);

    if (hasOwnLabel(order)) {
      try {
        await packUnderOwnLabel(order);
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
      return;
    }

    // Must happen inside the click gesture: after the ship await a popup blocker would kill it.
    const labelWindow = demo ? null : window.open('', '_blank');
    if (labelWindow) {
      labelWindow.document.write(
        '<!doctype html><title>Etykieta InPost</title>' +
          '<body style="font:16px system-ui;padding:24px">Tworzę etykietę w InPoście…</body>',
      );
    }

    try {
      // Demo mode refuses every admin write in api/client.ts, so a real request here could only
      // fail. Print the on-screen preview instead, so the packing flow is demonstrable.
      if (demo) {
        window.print();
        setNotice({
          kind: 'info',
          text: `Tryb demo: wydruk podglądu ${order.id} — nic nie poszło do InPostu.`,
        });
        advance();
        return;
      }
      const payload: ShipPayload =
        order.shippingMethod === 'kurier'
          ? { mode: 'shipx', method: 'kurier', size }
          : { mode: 'shipx', method: 'paczkomat', size };
      const result = await shipUntilNumbered(order.id, payload, (waitedMs) =>
        setNotice({
          kind: 'info',
          text: `InPost nadaje numer przesyłce — czekam (${Math.round(waitedMs / 1000)}s). Nie naciskaj Enter.`,
        }),
      );
      const labelUrl = result.labelUrl ?? labelUrlFor(order.id);
      if (labelWindow) labelWindow.location.href = labelUrl;
      setNotice({
        kind: 'info',
        text: `Nadano ${order.id} · ${result.trackingNumber}.`,
        labelUrl,
      });
      advance();
    } catch (err) {
      console.error('[kiosk] ship failed', err);
      labelWindow?.close();
      setNotice({ kind: 'error', text: shipErrorMessage(err) });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  // Manual ship fallback (#15) — currently unused in the prototype UI, kept here so
  // the "Wprowadź numer ręcznie" form can wire to it without a round-trip refactor.
  const manualShip = async (trackingNumber: string) => {
    if (!order) return;
    const method = order.shippingMethod === 'kurier' ? 'kurier' : 'paczkomat';
    try {
      await shipOrder(order.id, { mode: 'manual', method, trackingNumber });
      setSentToday((n) => n + 1);
      setCursor((c) => Math.min(c + 1, queue.length));
    } catch (err) {
      console.error('[kiosk] manual ship failed', err);
    }
  };
  void manualShip;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        // A focused button turns Enter into a click on its own; don't also ship from here.
        if ((e.target as HTMLElement | null)?.tagName === 'BUTTON') return;
        void print();
      }
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, size, queue.length]);

  if (!loaded) {
    return (
      <div className="logi" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div className="big" style={{ color: 'var(--paper)' }}>Ładuję kolejkę…</div>
      </div>
    );
  }

  if (queue.length === 0 || !order) {
    return (
      <div className="logi" style={{ alignItems: 'center', justifyContent: 'center' }}>
        {demo && <DemoBanner />}
        <div className="big" style={{ color: 'var(--orange)' }}>KOLEJKA PUSTA ✓</div>
        <div style={{ marginTop: 12, color: 'var(--paper)', opacity: 0.7, fontSize: 14 }}>
          Wszystkie opłacone zamówienia spakowane.
        </div>
        <button className="btn-l ghost" style={{ marginTop: 24 }} onClick={() => navigate('/admin')}>
          ↩ Wyjdź z trybu
        </button>
      </div>
    );
  }

  const ownLabel = hasOwnLabel(order);
  const paczkomat = order.shippingAddress.paczkomat;
  const courier = order.shippingAddress.courier;
  const international = order.shippingAddress.international;
  const deliveryTarget =
    order.shippingMethod === 'kurier'
      ? [courier?.street, [courier?.postalCode, courier?.city].filter(Boolean).join(' ')]
          .filter(Boolean)
          .join(', ')
          .toUpperCase() || '— BRAK ADRESU —'
      : order.shippingMethod === 'paczkomat'
        ? [paczkomat?.code, paczkomat?.city].filter(Boolean).join(' · ').toUpperCase() ||
          '— BRAK PACZKOMATU —'
        : order.shippingMethod === 'zagranica'
          ? `${(international?.countryName ?? international?.country ?? '— BRAK KRAJU —').toUpperCase()} · NR ${order.trackingNumber ?? '—'}`
          : 'WYDANIE NA KONCERCIE';

  return (
    <div className="logi">
      {demo && <DemoBanner />}
      <div className="logi-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <img src="/logo.png" alt="Operacja Teneryfa" style={{ width: 36, height: 'auto' }} />
          <span
            style={{
              fontFamily: 'var(--display)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              fontSize: 18,
              color: 'var(--orange-soft)',
            }}
          >
            Operacja Teneryfa
          </span>
          <span style={{ width: 1, height: 22, background: '#2A1B12' }} />
          <span
            style={{
              fontFamily: 'var(--display)',
              textTransform: 'uppercase',
              letterSpacing: '.18em',
              fontSize: 13,
              color: 'rgba(244,236,221,.6)',
            }}
          >
            LOGISTYKA
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: 'rgba(244,236,221,.7)' }}>
          <span className="mono">{fmtClock(now)}</span>
          <span style={{ width: 1, height: 18, background: '#2A1B12' }} />
          <span>
            Kolejka:{' '}
            <b style={{ color: 'var(--orange)', fontSize: 16 }}>{queue.length - cursor}</b>
          </span>
          <span>
            Wysłane dziś: <b style={{ color: '#7ED957', fontSize: 16 }}>{sentToday}</b>
          </span>
          <button
            className="btn-l ghost"
            style={{ fontSize: 13, padding: '6px 10px' }}
            onClick={() => navigate('/admin')}
          >
            ↩ Wyjdź z trybu
          </button>
        </div>
      </div>

      <div className="logi-body">
        <div className="logi-left">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div
                className="mono"
                style={{ fontSize: 12, color: 'rgba(244,236,221,.5)', letterSpacing: '.15em' }}
              >
                ZAMÓWIENIE {cursor + 1} / {queue.length}
              </div>
              <div className="big" style={{ marginTop: 6, color: 'var(--paper)' }}>
                {order.id.replace('OPT-26-', 'OPT-26-')}
                <span style={{ color: 'var(--orange)' }}>
                  {/* split last 4 in orange */}
                </span>
              </div>
              <div style={{ marginTop: 8, fontSize: 16 }}>
                {order.customerName} · {order.externalOrderId ?? '— bez zamówienia —'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div
                className="mono"
                style={{
                  fontSize: 10,
                  color: 'rgba(244,236,221,.5)',
                  letterSpacing: '.15em',
                  marginBottom: 6,
                }}
              >
                DOSTAWA · WYBÓR KLIENTA
              </div>
              <div className="acct">{METHOD_TAG[order.shippingMethod]}</div>
              <div
                className="mono"
                style={{ fontSize: 11, marginTop: 6, color: 'var(--orange-soft)' }}
              >
                {deliveryTarget}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 28 }}>
            <h2>Do spakowania</h2>
            <div style={{ background: '#150D08', border: '1px solid #2A1B12', padding: 18 }}>
              {order.items.map((s, i) => (
                <div key={i} className="row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                    <div
                      style={{
                        width: 44,
                        height: 44,
                        background: s.color === 'czarna' ? '#0E0807' : '#F4ECDD',
                        border: '2px solid var(--orange)',
                      }}
                    />
                    <div>
                      <div className="display" style={{ fontSize: 24, color: 'var(--paper)' }}>
                        KOSZULKA · {s.color.toUpperCase()}
                        {s.fromOrderId ? (
                          <span
                            className="mono"
                            style={{ fontSize: 11, color: 'var(--orange)', marginLeft: 8 }}
                          >
                            Z {s.fromOrderId}
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="mono"
                        style={{
                          fontSize: 11,
                          color: 'rgba(244,236,221,.55)',
                          letterSpacing: '.1em',
                          marginTop: 2,
                        }}
                      >
                        KRÓJ {s.cut.toUpperCase()} · 220G · SITODRUK
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span className="size">{s.size}</span>
                    <span className="qty">×{s.qty}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 22 }}>
            <div>
              {ownLabel ? (
                <OwnLabelNote order={order} />
              ) : (
                <>
                  <h2>
                    {order.shippingMethod === 'kurier'
                      ? 'Rozmiar paczki kurierskiej'
                      : 'Gabaryt skrytki paczkomatu'}
                  </h2>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {PARCEL_SIZES.map((s) => {
                      const active = s === size;
                      return (
                        <button
                          key={s}
                          onClick={() => setSizeOverride({ orderId: order.id, size: s })}
                          style={{
                            flex: 1,
                            padding: '18px 0',
                            cursor: 'pointer',
                            fontFamily: 'var(--display)',
                            fontSize: 24,
                            letterSpacing: '.06em',
                            background: active ? 'var(--orange)' : 'transparent',
                            color: active ? 'var(--ink)' : '#F4ECDD',
                            border: active
                              ? '2px solid var(--orange)'
                              : '2px solid rgba(244,236,221,.2)',
                          }}
                        >
                          GAB. {s}
                          <div
                            style={{
                              fontFamily: 'var(--mono)',
                              fontSize: 10,
                              marginTop: 4,
                              opacity: 0.7,
                              letterSpacing: '.1em',
                            }}
                          >
                            {SIZE_DIMENSIONS[s]}
                          </div>
                          {s === suggestedSize && (
                            <div
                              style={{
                                fontFamily: 'var(--mono)',
                                fontSize: 9,
                                marginTop: 3,
                                opacity: active ? 0.75 : 0.5,
                                letterSpacing: '.12em',
                              }}
                            >
                              SUGESTIA
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div
                    className="mono"
                    style={{
                      fontSize: 10,
                      marginTop: 8,
                      letterSpacing: '.08em',
                      color: 'rgba(244,236,221,.45)',
                    }}
                  >
                    {`${overridden ? 'ZMIANA RĘCZNA · ' : ''}SUGESTIA WG LICZBY KOSZULEK: GAB. ${suggestedSize} (${totalShirts} SZT.)`}
                  </div>
                </>
              )}
              {order.unpaidRideAlongs.length > 0 && (
                <div
                  className="mono"
                  style={{
                    marginTop: 8,
                    padding: 10,
                    border: '1px solid var(--orange)',
                    background: 'rgba(255,107,26,.1)',
                    fontSize: 10,
                    lineHeight: 1.5,
                    letterSpacing: '.08em',
                    color: 'var(--orange-soft)',
                  }}
                >
                  CZEKA NA TĘ PACZKĘ BEZ OPŁATY: {order.unpaidRideAlongs.join(', ')}
                  <br />
                  NIEOPŁACONE — KOSZULEK NIE MA W PUDEŁKU. ROZWIĄŻ TO ZAMÓWIENIE PRZED WYSYŁKĄ.
                </div>
              )}
            </div>
            <div>
              <h2>Komentarz klienta</h2>
              <div
                style={{
                  background: '#150D08',
                  border: '1px dashed rgba(244,236,221,.2)',
                  padding: 12,
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: 'rgba(244,236,221,.85)',
                  minHeight: 90,
                }}
              >
                {order.customerCommentText ? (
                  <>
                    <span
                      className="mono"
                      style={{ fontSize: 10, color: 'var(--orange-soft)', letterSpacing: '.15em' }}
                    >
                      TYP:{' '}
                      {order.customerCommentTopic === 'combine'
                        ? 'POŁĄCZENIE'
                        : order.customerCommentTopic === 'addto'
                          ? 'DOKUP'
                          : 'INNA SPRAWA'}
                    </span>
                    <br />"{order.customerCommentText}"
                  </>
                ) : (
                  <span style={{ color: 'rgba(244,236,221,.4)' }}>— bez komentarza —</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="logi-right">
          <h2>{ownLabel ? 'Podgląd wydruku · etykieta własna · A6 poziomo' : 'Podgląd wydruku · A6'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14 }}>
            {ownLabel ? (
              <div style={{ zoom: 0.8 }}>
                <OwnLabel order={order} demo={demo} />
              </div>
            ) : (
              <InPostLabel
                order={order}
                packageSize={size}
                totalShirts={totalShirts}
                demo={demo}
              />
            )}
          </div>
        </div>
      </div>

      <div className="logi-bottom">
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-l danger" onClick={() => navigate(`/admin/zamowienia/${order.id}`)}>
            ✎ Edytuj zamówienie
          </button>
          <button className="btn-l ghost" onClick={skip}>
            ⏭ Pomiń
          </button>
        </div>
        {notice && (
          <div
            role={notice.kind === 'error' ? 'alert' : 'status'}
            className="mono"
            style={{
              flex: 1,
              margin: '0 18px',
              padding: '8px 12px',
              fontSize: 12,
              lineHeight: 1.4,
              border: `1px solid ${notice.kind === 'error' ? '#FF8A6B' : 'var(--orange)'}`,
              color: notice.kind === 'error' ? '#FF8A6B' : '#FFD9A8',
              background: '#1A0F09',
            }}
          >
            {notice.text}
            {notice.labelUrl && (
              <>
                {' '}
                <a
                  href={notice.labelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--orange)' }}
                >
                  OTWÓRZ ETYKIETĘ
                </a>
              </>
            )}
            {notice.reprint && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => notice.reprint && reprintOwnLabel(notice.reprint)}
                  style={{
                    background: 'none',
                    border: 0,
                    padding: 0,
                    font: 'inherit',
                    color: 'var(--orange)',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  DRUKUJ PONOWNIE
                </button>
              </>
            )}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div
            className="mono"
            style={{
              fontSize: 11,
              color: 'rgba(244,236,221,.5)',
              letterSpacing: '.1em',
              textAlign: 'right',
            }}
          >
            ENTER · DRUKUJ I NAST.
            <br />
            ESC · POMIŃ
          </div>
          <button className="btn-l" style={{ minWidth: 320 }} onClick={print} disabled={busy}>
            {busy
              ? ownLabel
                ? '⏳ ZAPISUJĘ…'
                : '⏳ NADAJĘ…'
              : demo
                ? '⎙ SYMULUJ DRUK + WYSYŁKĘ →'
                : order.shippingMethod === 'odbior'
                  ? '⎙ DRUKUJ + DO ODBIORU →'
                  : order.shippingMethod === 'zagranica'
                    ? '⎙ DRUKUJ + NADAJ ZA GRANICĘ →'
                    : '⎙ DRUKUJ + WYŚLIJ INPOST →'}
          </button>        </div>
      </div>
    </div>
  );
}

function fmtClock(d: Date) {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${dd}.${mm}.${yy} · ${hh}:${mi}:${ss}`;
}

function OwnLabelNote({ order }: { order: LogisticsQueueOrder }) {
  return (
    <>
      <h2>Etykieta własna · nie InPost</h2>
      <div
        className="mono"
        style={{
          padding: 12,
          border: '1px solid rgba(244,236,221,.2)',
          fontSize: 11,
          lineHeight: 1.6,
          letterSpacing: '.06em',
          color: 'rgba(244,236,221,.75)',
        }}
      >
        {order.shippingMethod === 'odbior'
          ? 'PO WYDRUKU ZAMÓWIENIE TRAFIA DO ODBIORU OSOBISTEGO, A KLIENT DOSTAJE MAILA Z INSTRUKCJĄ ODBIORU.'
          : `PO WYDRUKU ZAMÓWIENIE JEST NADANE Z NUMEREM ${order.trackingNumber ?? '—'} — KLIENT DOSTAJE GO MAILEM.`}
      </div>
    </>
  );
}

function InPostLabel({
  order,
  packageSize,
  totalShirts,
  demo,
}: {
  order: LogisticsQueueOrder;
  packageSize: ParcelSize;
  totalShirts: number;
  demo: boolean;
}) {
  const recipient = useMemo(() => {
    const addr = order.shippingAddress;
    if (order.shippingMethod === 'paczkomat') {
      const p = addr.paczkomat;
      return {
        line1: `PACZKOMAT ${p?.code ?? ''}`,
        line2: p?.street ?? '',
        line3: [p?.postCode, p?.city].filter(Boolean).join(' '),
      };
    }
    return {
      line1: addr.courier?.street ?? '',
      line2: `${addr.courier?.postalCode ?? ''} ${addr.courier?.city ?? ''}`,
      line3: '',
    };
  }, [order]);

  const tracking = order.trackingNumber ?? '—';

  return (
    <div className="label" style={{ width: 360 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          borderBottom: '2px solid #000',
          paddingBottom: 8,
        }}
      >
        <div
          style={{
            background: '#FFD500',
            color: '#000',
            padding: '4px 6px',
            fontWeight: 800,
            fontSize: 11,
            letterSpacing: 0.5,
          }}
        >
          InPost
        </div>
      </div>

      <div style={{ padding: '10px 0', borderBottom: '1px solid #000' }}>
        <div style={{ fontSize: 9, letterSpacing: 1 }}>ODBIORCA / RECIPIENT</div>
        <div style={{ fontSize: 14, fontWeight: 800, marginTop: 4 }}>{order.customerName.toUpperCase()}</div>
        <div style={{ fontSize: 12, marginTop: 2 }}>TEL {order.phone}</div>
        <div style={{ fontSize: 14, fontWeight: 800, marginTop: 6 }}>{recipient.line1}</div>
        <div style={{ fontSize: 11 }}>{recipient.line2}</div>
        <div style={{ fontSize: 11 }}>{recipient.line3}</div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 0',
          borderBottom: '1px solid #000',
        }}
      >
        <div>
          <div style={{ fontSize: 9, letterSpacing: 1 }}>NR PRZESYŁKI</div>
          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 2 }}>{tracking}</div>
          <div style={{ fontSize: 9, marginTop: 2 }}>GAB. {packageSize}</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(10, 6px)',
              gridAutoRows: '6px',
              gap: 0,
            }}
          >
            {Array.from({ length: 100 }).map((_, i) => (
              <div key={i} style={{ background: (i * 37 + i * i) % 5 < 2 ? '#000' : '#fff' }} />
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: '10px 0' }}>
        <svg viewBox="0 0 320 36" width="100%" height={36}>
          {Array.from({ length: 80 }).map((_, i) => {
            const w = ((i * 13) % 5) + 1;
            const x = i * 4;
            return (
              <rect key={i} x={x} y="0" width={w} height={36} fill="#000" opacity={i % 2 ? 1 : 0} />
            );
          })}
        </svg>
        <div
          style={{
            textAlign: 'center',
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 2,
            marginTop: 2,
          }}
        >
          {tracking}
        </div>
      </div>

      <div style={{ borderTop: '2px dashed #000', margin: '10px 0', position: 'relative' }}>
        <span
          style={{
            position: 'absolute',
            top: -8,
            right: 0,
            background: '#fff',
            padding: '0 6px',
            fontSize: 9,
            letterSpacing: 1,
          }}
        >
          ✂ ODERWIJ ✂
        </span>
      </div>

      <div>
        <div style={{ fontSize: 9, letterSpacing: 1, fontWeight: 800 }}>ZAWARTOŚĆ · DLA PAKOWACZA</div>
        <div style={{ fontFamily: 'var(--display)', fontSize: 16, marginTop: 4, color: '#D14E0C' }}>
          {order.id} · {totalShirts} SZT
        </div>
        <table style={{ width: '100%', fontSize: 11, marginTop: 6, borderCollapse: 'collapse' }}>
          <tbody>
            {order.items.map((item, i, arr) => (
              <tr
                key={i}
                style={{ borderBottom: i < arr.length - 1 ? '1px dashed #888' : 'none' }}
              >
                <td style={{ padding: '3px 0', fontWeight: 800 }}>
                  KOSZULKA · {item.color.toUpperCase()} · {item.cut.toUpperCase()}
                </td>
                <td style={{ textAlign: 'center', fontWeight: 800 }}>{item.size}</td>
                <td style={{ textAlign: 'right' }}>×{item.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 6, fontSize: 9, color: '#666' }}>
          {order.mergedIntoId ? `POŁĄCZONE Z ${order.mergedIntoId} · ` : ''}
          ZAM. {order.externalOrderId ?? '—'}
        </div>
        {demo && (
          <div className="label-demo-stamp">
            DEMO · PODGLĄD · NIE JEST ETYKIETĄ PRZEWOZOWĄ
          </div>
        )}
      </div>
    </div>
  );
}
